<template>
    <section class="card-responsible-settings">
        <h2>К кому обратиться по карточкам и возврату из резерва</h2>
        <p>Эти контакты увидят в табеле, если не найдена карточка сотрудника или сотрудник находится в резерве.
            Назначение ответственным не выдаёт права управлять сотрудниками и трудовыми периодами.</p>
        <MultipleParticipantsConfig
            :value="value" :users="users" :departments="departments"
            :allowed-types="['specific_users', 'department', 'object_role']"
            :show-object-roles="true" :object-role-options="objectRoles"
            :show-fallback-option="false" :show-priority="false" :show-notification-mode="false"
            participant-label="ответственного"
            hint-text="Отделы и роли раскрываются в конкретных людей. В табеле показываются только действующие пользователи с правом создания карточек."
            object-role-hint-text="В подсказке используется текущая роль на объекте этого табеля."
            @input="updateSelection"
        />
        <div class="card-responsible-settings__preview" aria-live="polite">
            <p v-if="loading" role="status"><b-spinner small /> Проверяем текущие права…</p>
            <b-alert v-if="error" show variant="danger">
                {{ error }} <b-button size="sm" variant="outline-danger" @click="refresh">Повторить</b-button>
            </b-alert>
            <template v-if="preview">
                <h3>Доступные ответственные · {{ preview.people.length }}</h3>
                <p v-if="preview.people.length" class="small">
                    Стрелками задайте порядок конкретных людей, в том числе внутри отдела.
                    Этот порядок сохранится вместе с настройками и будет использован в подсказке табеля.
                    <b-button v-if="value.user_order && value.user_order.length" size="sm" variant="link" class="p-0"
                        :disabled="loading" @click="resetDisplayOrder">Вернуть автоматический порядок</b-button>
                </p>
                <ol v-if="preview.people.length" class="card-responsible-settings__people">
                    <li v-for="(person, index) in preview.people" :key="person.user_id" class="card-responsible-settings__person">
                        <span class="card-responsible-settings__number">{{ index + 1 }}.</span>
                        <div class="card-responsible-settings__identity">
                            <EmployeeLink :user-id="person.user_id" :fallback="person.name" compact />
                            <span v-if="person.labels.length"> · {{ person.labels.join(' · ') }}</span>
                        </div>
                        <b-button-group size="sm">
                            <b-button variant="outline-secondary" :disabled="loading || index === 0"
                                :aria-label="`Выше: ${person.name}`" title="Переместить выше" @click="movePerson(index, -1)">
                                <span aria-hidden="true">↑</span>
                            </b-button>
                            <b-button variant="outline-secondary" :disabled="loading || index === preview.people.length - 1"
                                :aria-label="`Ниже: ${person.name}`" title="Переместить ниже" @click="movePerson(index, 1)">
                                <span aria-hidden="true">↓</span>
                            </b-button>
                        </b-button-group>
                    </li>
                </ol>
                <p v-else>Сейчас в подсказке нет доступных ответственных.</p>
                <b-alert v-if="preview.excluded.length || preview.warnings.length" show variant="warning">
                    <strong>Настройку можно сохранить. Эти назначения пока не дадут контакт в табеле:</strong>
                    <ul class="mb-0 mt-2">
                        <li v-for="person in preview.excluded" :key="person.user_id">
                            <EmployeeLink :user-id="person.user_id" :fallback="person.name" compact />
                            <span v-if="person.labels.length"> · {{ person.labels.join(' · ') }}</span>
                            <div>{{ person.reason }} — в подсказке табеля показываться не будет.</div>
                        </li>
                        <li v-for="warning in preview.warnings" :key="warning">{{ warning }}</li>
                    </ul>
                </b-alert>
            </template>
        </div>
    </section>
</template>

<script>
import api from '@/utils/api';
import EmployeeLink from '@/components/EmployeeLink.vue';
import MultipleParticipantsConfig from '@/views/Requests/RequestTypes/MultipleParticipantsConfig/MultipleParticipantsConfig.vue';

export default {
    name: 'TimesheetEmployeeCardSettings',
    components: {EmployeeLink, MultipleParticipantsConfig},
    props: {
        value: {type: Object, default: () => ({participants: []})},
        users: {type: Array, default: () => []},
        departments: {type: Array, default: () => []}
    },
    data() {
        return {loading: false, error: '', preview: null, requestVersion: 0, timer: null,
            objectRoles: [{value: 'main_staff', text: 'Прораб'}, {value: 'curator', text: 'Куратор объекта'},
                {value: 'has_access', text: 'Имеющий доступ к объекту'}]};
    },
    watch: {
        value: {deep: true, immediate: true, handler(value, previous) {
            this.requestVersion += 1;
            clearTimeout(this.timer);
            if (!previous || JSON.stringify(value.participants) !== JSON.stringify(previous.participants)) this.preview = null;
            this.error = '';
            this.loading = true;
            this.timer = setTimeout(() => this.refresh(), 300);
        }}
    },
    beforeDestroy() {
        clearTimeout(this.timer);
        this.requestVersion += 1;
    },
    methods: {
        updateSelection(value) {
            this.$emit('input', {participants: value.participants || [], user_order: this.value.user_order || []});
        },
        movePerson(index, direction) {
            if (this.loading || !this.preview) return;
            const people = this.preview.people.slice();
            const target = index + direction;
            if (!people[index] || target < 0 || target >= people.length) return;
            people.splice(target, 0, people.splice(index, 1)[0]);
            const visibleIds = new Set(people.map(person => Number(person.user_id)));
            const remaining = people.map(person => Number(person.user_id));
            // Keep hidden people's slots so restoring their permission also restores their place.
            const order = (this.value.user_order || []).map(Number)
                .map(id => visibleIds.has(id) ? remaining.shift() : id).concat(remaining);
            this.preview = Object.assign({}, this.preview, {people});
            this.$emit('input', {participants: this.value.participants || [], user_order: order});
        },
        resetDisplayOrder() {
            this.$emit('input', {participants: this.value.participants || []});
        },
        async refresh() {
            clearTimeout(this.timer);
            const version = ++this.requestVersion;
            this.loading = true;
            this.error = '';
            try {
                const response = await api.post('/api/timesheets/settings/employee-card-responsibles/preview',
                    this.value, {ignoreModal: true});
                if (version === this.requestVersion) this.preview = response.data;
            } catch (error) {
                if (version === this.requestVersion) {
                    this.preview = null;
                    this.error = error.response && error.response.data && error.response.data.msg || 'Не удалось проверить права ответственных.';
                }
            } finally {
                if (version === this.requestVersion) this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
.card-responsible-settings { max-width: 960px; }
.card-responsible-settings h2 { font-size: 1.15rem; }
.card-responsible-settings h3 { font-size: 1rem; }
.card-responsible-settings__preview { margin-top: 1rem; overflow-wrap: anywhere; }
.card-responsible-settings li { margin-bottom: .5rem; }
.card-responsible-settings__people { list-style: none; padding: 0; }
.card-responsible-settings__person { display: flex; align-items: flex-start; gap: .5rem; padding: .5rem 0; border-bottom: 1px solid #e2e8f0; }
.card-responsible-settings__number { color: #687888; padding-top: .25rem; }
.card-responsible-settings__identity { flex: 1; min-width: 0; }
.card-responsible-settings__person .btn-group { flex-shrink: 0; }
</style>
